import { animate } from '@lit-labs/motion';
import { localized } from '@weavedev/lit-i18next';
import { css, CSSResultGroup, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';

import {
  ColorCollapsibleStepDto,
  ColorStepDto,
  ColorVersionStepDto,
  PrintOptionCollapsibleStepDto,
  PrintOptionPerPositionStepDto,
  PrintOptionStepDto,
  ProductionSpeedStepDto,
  QuantityCollapsibleStepDto,
  QuantityPerSizePerColorCollapsibleStepDto,
  QuantityPerSizePerColorStepDto,
  QuantityPerVariantStepDto,
  QuantityStepDto,
  StepEnum,
  VersionCollapsibleStepDto,
  VersionStepDto,
} from '@/api-sdk';
import {
  ConfiguratorContextType,
  ValidationConfiguratorContextType,
} from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { buttonStyles, rootVariablesStyles } from '@/styles';
import { ActionEvent, Step } from '@/types';
import { updateValidationConfiguratorContext } from '@/utils';
import { ConfiguratorMixin } from './configurator-mixin';
import { goToStep, setScrollDelay } from './utils';

import '../accessibility/fg-aria-live';
import '../loader/fg-loader';
import '../login/fg-login-cta';
import '../messages/fg-message-snack-bar';
import './fg-configurator-action-sections';
import './fg-configurator-actions';
import './fg-configurator-error';
import './mobile/fg-configurator-mobile-actions';
import './mobile/fg-configurator-mobile-done';
import './mobile/fg-configurator-mobile-header';
import './mobile/fg-configurator-mobile-navigation';
import './step-collapsible/color/fg-color-stacked';
import './step-collapsible/print-option/fg-print-option-stacked';
import './step-collapsible/quantity/fg-quantity-stacked';
import './step-collapsible/version/fg-version-stacked';
import './step/fg-configurator-step-color';
import './step/fg-configurator-step-color-version';
import './step/fg-configurator-step-print-option';
import './step/fg-configurator-step-production-speed';
import './step/fg-configurator-step-quantity';
import './step/fg-configurator-step-version';
import './step/wholesale/fg-configurator-print-option-per-position';
import './step/wholesale/fg-configurator-quantity-variants';

/**
 * @element fg-configurator
 */
@customElement('fg-configurator')
@localized()
export class FgConfigurator extends ConfiguratorMixin(LitElement) {
  renderStep(step: Step, stepIndex: number) {
    const { showValidationMessages } = this.validationConfiguratorContext;
    switch (step.type) {
      case 'color': {
        const {
          type,
          colors,
          selectMode,
          selectModeSwitchQueryString,
          validationMessage,
        } = step as ColorStepDto;
        return html`
          <fg-configurator-step-color
            class="config-step"
            id=${type}
            .colorOptions=${colors}
            .selectMode=${selectMode}
            .selectModeSwitchQueryString=${selectModeSwitchQueryString}
            .validationMessage=${validationMessage}
            .showValidationMessage=${showValidationMessages[type]}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @default-color-selected=${this.onDefaultColorSelected}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @color-selected=${(event: CustomEvent) =>
              (this.selectedColor = event.detail)}
          ></fg-configurator-step-color>
        `;
      }
      case 'color_collapsible': {
        const stepData = step as ColorCollapsibleStepDto;
        const hasOpenColorSelection = Array.isArray(stepData.colorSelections)
          ? stepData.colorSelections.some(selection => selection.open)
          : false;
        return html`
          <fg-color-stacked
            class="config-step ${classMap({
              valid: stepData.valid,
              active: hasOpenColorSelection,
            })}"
            id=${stepData.type}
            .stepIndex=${stepIndex}
            .stepData=${stepData}
            .showValidationMessage=${showValidationMessages[stepData.type]}
            .scrollToStep=${this.scrollToValidationMessage(stepData.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, stepData.type)}
            @focus-to-next-step=${(event: CustomEvent) =>
              this.goToStepOnSelection(event?.currentTarget)}
          ></fg-color-stacked>
        `;
      }
      case 'color_version': {
        const {
          type,
          colors,
          versions,
          selectModeSwitchQueryString,
          selectMode,
          validationMessage,
        } = step as ColorVersionStepDto;
        return html`
          <fg-configurator-step-color-version
            class="config-step"
            id=${type}
            .colorOptions=${colors}
            .versionOptions=${versions}
            .selectMode=${selectMode}
            .selectModeSwitchQueryString=${selectModeSwitchQueryString}
            .validationMessage=${validationMessage}
            .showValidationMessage=${showValidationMessages[step.type]}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @default-color-selected=${this.onDefaultColorSelected}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @color-selected=${(event: CustomEvent) =>
              (this.selectedColor = event.detail)}
          ></fg-configurator-step-color-version>
        `;
      }
      case 'version': {
        const { type, versions, validationMessage } = step as VersionStepDto;
        return html`
          <fg-configurator-step-version
            class="config-step"
            id=${type}
            .versions=${versions}
            .validationMessage=${validationMessage}
            .showValidationMessage=${showValidationMessages[type]}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
          ></fg-configurator-step-version>
        `;
      }
      case 'version_collapsible': {
        const stepData = step as VersionCollapsibleStepDto;

        return html`
          <fg-version-stacked
            class="config-step ${classMap({
              valid: stepData.valid,
              active: stepData.open,
            })}"
            id=${stepData.type}
            .stepIndex=${stepIndex}
            .stepData=${stepData}
            .showValidationMessage=${showValidationMessages[stepData.type]}
            .scrollToStep=${this.scrollToValidationMessage(stepData.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, stepData.type)}
          ></fg-version-stacked>
        `;
      }
      case 'print_option': {
        const {
          type,
          selectedOptions,
          cancelQueryString,
          unprintedQueryString,
          addPrintOptionQueryString,
          unprinted,
          validationMessage,
          printOptionFilter,
        } = step as PrintOptionStepDto;

        return html`
          <fg-configurator-step-print-option
            class="config-step"
            id=${type}
            .selectedOptions=${selectedOptions}
            .cancelQueryString=${cancelQueryString}
            .unprintedQueryString=${unprintedQueryString}
            .addPrintOptionQueryString=${addPrintOptionQueryString}
            .unprinted=${unprinted}
            .isLoading=${this.getGetConfiguratorPromise !== null}
            .printOptionFilter=${printOptionFilter}
            .validationMessage=${validationMessage}
            .showValidationMessage=${showValidationMessages[type]}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @configurator-context-update=${(
              event: CustomEvent<{ context: ConfiguratorContextType }>,
            ) => {
              this.configuratorContext = event.detail.context;
              this.requestUpdate();
            }}
            @validation-configurator-context-update=${(
              event: CustomEvent<{
                context: ValidationConfiguratorContextType;
              }>,
            ) => {
              this.validationConfiguratorContext = event.detail.context;
              this.requestUpdate();
            }}
          ></fg-configurator-step-print-option>
        `;
      }
      case 'print_option_collapsible': {
        const stepData = step as PrintOptionCollapsibleStepDto;
        const hasOpenPrintOptionSelection = Array.isArray(stepData.printOptions)
          ? stepData.printOptions.some(selection => selection.open)
          : false;
        return html`
          <fg-print-option-stacked
            class="config-step ${classMap({
              valid: stepData.valid,
              active: hasOpenPrintOptionSelection,
            })}"
            id=${stepData.type}
            .stepData=${stepData}
            .showValidationMessage=${showValidationMessages[stepData.type]}
            .scrollToStep=${this.scrollToValidationMessage(stepData.type)}
            @option-selected=${(event: CustomEvent) => {
              this.onOptionSelected(event, stepData.type);
            }}
            @focus-to-next-step=${(e: CustomEvent) => {
              this.goToStepOnSelection(e.currentTarget);
            }}
            @handle-announcement=${(event: CustomEvent) =>
              (this.announcement = event.detail.announcement)}
          ></fg-print-option-stacked>
        `;
      }
      case 'quantity': {
        const { type } = step as QuantityStepDto;
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${type}
            .queryString=${this.queryString}
            .quantityStep=${type}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_collapsible': {
        const stepData = step as QuantityCollapsibleStepDto;
        return html`
          <fg-quantity-stacked
            class="config-step ${classMap({
              valid: this.quantityValidated,
              active: stepData.details.open,
            })}"
            id=${stepData.type}
            .queryString=${this.queryString}
            .details=${stepData.details}
            .quantityStep=${stepData.type}
            .scrollToStep=${this.scrollToValidationMessage(stepData.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, stepData.type)}
            @focus-to-next-step=${(event: CustomEvent) =>
              this.goToStepOnSelection(event.currentTarget)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
          ></fg-quantity-stacked>
        `;
      }
      case 'quantity_per_color':
      case 'quantity_per_size': {
        const { type } = step;
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${type}
            .queryString=${this.queryString}
            .quantityStep=${type}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_per_color_collapsible':
      case 'quantity_per_size_collapsible': {
        const stepData = step as QuantityCollapsibleStepDto;
        return html`
          <fg-quantity-stacked
            class="config-step ${classMap({
              valid: this.quantityValidated,
              active: stepData.details.open,
            })}"
            id=${stepData.type}
            .queryString=${this.queryString}
            .details=${stepData.details}
            .quantityStep=${stepData.type}
            .scrollToStep=${this.scrollToValidationMessage(stepData.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, stepData.type)}
            @focus-to-next-step=${(event: CustomEvent) =>
              this.goToStepOnSelection(event.currentTarget)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
          ></fg-quantity-stacked>
        `;
      }
      case 'quantity_per_size_per_color': {
        const { type, colors } = step as QuantityPerSizePerColorStepDto;
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${type}
            .colors=${colors}
            .queryString=${this.queryString}
            .quantityStep=${type}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_per_size_per_color_collapsible': {
        const { type, colors, details } =
          step as QuantityPerSizePerColorCollapsibleStepDto;
        return html`
          <fg-quantity-stacked
            class="config-step ${classMap({
              valid: this.quantityValidated,
              active: details.open,
            })}"
            id=${type}
            .colors=${colors}
            .queryString=${this.queryString}
            .details=${details}
            .quantityStep=${type}
            .scrollToStep=${this.scrollToValidationMessage(type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @focus-to-next-step=${(e: CustomEvent) =>
              this.goToStepOnSelection(e.currentTarget)}
            @quantity-set=${(event: CustomEvent) => {
              this.onQuantitySet(event);
            }}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-quantity-stacked>
        `;
      }

      case 'quantity_per_variant': {
        const { type } = step as QuantityPerVariantStepDto;
        return html`
          <fg-configurator-quantity-variant
            part=${this.globalContext.layout}
            class="config-step"
            id=${type}
            .stepType=${type as StepEnum.quantity_per_variant}
            .renderData=${step as QuantityPerVariantStepDto}
            .queryString=${this.queryString}
            @quantity-validation=${this.validateQuantity}
            @quantity-set=${this.onQuantitySet}
            @validate-variant-stock=${this.validateStockPerVariant}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-quantity-variant>
        `;
      }
      case 'print_option_per_position': {
        const { type, positions, visualDesigner } =
          step as PrintOptionPerPositionStepDto;
        this.configuratorContext = {
          ...this.configuratorContext,
          visualDesigner: visualDesigner,
        };
        return html`
          <fg-configurator-print-option-per-position
            part=${this.globalContext.layout}
            class="config-step"
            id=${type}
            .stepType=${type}
            .printPositions=${positions}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-print-option-per-position>
        `;
      }
      case 'production_speed': {
        const { type, options } = step as ProductionSpeedStepDto;

        if (!this.salable) {
          return nothing;
        }
        return html`
          <fg-configurator-step-production-speed
            part=${this.globalContext.layout}
            class="config-step"
            id=${type}
            .options=${options}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, type)}
          ></fg-configurator-step-production-speed>
        `;
      }
    }
  }

  handleToSave(eventToSaveName: ActionEvent) {
    this.eventToSave = eventToSaveName;
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configurator"></fg-loader>
      `;
    }

    if (this.error) {
      return html`
        <fg-configurator-error></fg-configurator-error>
      `;
    }

    const { layout, loggedIn } = this.globalContext;
    const { salable } = this.configuratorContext;
    const { showValidationSnackbar } = this.validationConfiguratorContext;
    const { isMobile } = this.mobileController;

    if (!this.steps.length) {
      return null;
    }

    return html`
      <fg-configurator-mobile-actions
        .queryString=${this.queryString}
        .configuration=${this.configuration}
        .quantityValidated=${this.quantityValidated}
        @reset-configurator=${this.resetConfiguration}
        @toggle-configurator=${this.toggleConfigurator}
        @set-mode=${this.setMode}
      ></fg-configurator-mobile-actions>
      <fg-aria-live .announcement=${this.announcement}></fg-aria-live>
      <div
        class="wrapper ${classMap({
          show: this.toggleMobileConfigurator,
          hidden: !this.toggleMobileConfigurator,
          oneColumn: !salable && loggedIn === true,
        })}"
        part=${layout}
        ${animate({ properties: ['transform', 'opacity'] })}
        ${ref(this.configuratorWrapper)}
      >
        ${isMobile
          ? html`
              <fg-configurator-mobile-header
                part=${layout}
                @toggle-configurator=${this.toggleConfigurator}
                .currentStep=${this.activeStep}
                .selectedColor=${this.selectedColor}
                .stepType=${this.steps[this.activeStep]?.type}
              ></fg-configurator-mobile-header>
            `
          : null}
        <div class="configurator" tabindex="0" ${ref(this.configuratorElement)}>
          ${this.steps.map((step, index) => {
            const preventProductionSpeedRender =
              step.type === 'production_speed' &&
              !salable &&
              layout === 'wholesale';

            return html`
              ${this.renderStep(step, index)}
              ${index < this.steps.length - 1 &&
              layout === 'wholesale' &&
              !preventProductionSpeedRender
                ? html`
                    <span class="delimiter"></span>
                  `
                : null}
            `;
          })}
          ${isMobile && layout === 'retail'
            ? html`
                <fg-configurator-mobile-done
                  class="config-step"
                ></fg-configurator-mobile-done>
              `
            : null}
        </div>

        <fg-configurator-action-sections
          part=${layout}
          .currentStep=${this.activeStep}
          .steps=${this.steps}
        >
          ${!isMobile && layout === 'wholesale'
            ? html`
                <fg-login-cta
                  slot="configurator-actions"
                  part=${layout}
                ></fg-login-cta>
              `
            : nothing}
          <fg-configurator-mobile-navigation
            part=${layout}
            .currentStep=${layout === 'retail'
              ? this.activeStep
              : this.activeStep + 1}
            .debtor=${this.debtor}
            .country=${this.country}
            .steps=${this.steps}
            .validSteps=${this.validSteps}
            .quantityValidated=${this.quantityValidated}
            .configuration=${this.configuration}
            @prev-step=${() => {
              this.activeStep--;
              goToStep(
                this.configuratorElement.value,
                this.activeStep,
                setScrollDelay(this.steps[this.activeStep].type),
              );
            }}
            @next-step=${() => {
              this.activeStep++;
              goToStep(
                this.configuratorElement.value,
                this.activeStep,
                setScrollDelay(this.steps[this.activeStep]?.type || null),
              );
            }}
            @trigger-validation-messages=${this.toggleValidationLogic}
            slot="mobile-navigation"
          ></fg-configurator-mobile-navigation>
          <fg-configurator-actions
            slot="configurator-actions"
            .configuration=${this.configuration}
            .unprinted=${this.unprinted}
            .eventToSave=${this.eventToSave}
            .product=${this.product}
            .quantityValidated=${this.quantityValidated}
            .queryString=${this.queryString}
            @trigger-validation-messages=${this.toggleValidationLogic}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, StepEnum.production_speed)}
            @prev-step=${() => {
              this.activeStep--;
              goToStep(this.configuratorElement.value, this.activeStep);
            }}
          ></fg-configurator-actions>
        </fg-configurator-action-sections>
      </div>
      ${!this.isConfiguratorLoaded
        ? html`
            <fg-loader type="in-page"></fg-loader>
          `
        : null}
      <fg-message-snack-bar
        part=${layout}
        ?show=${Boolean(showValidationSnackbar)}
        .type=${'error'}
        message=${showValidationSnackbar || ''}
        @on-close=${() => {
          this.validationConfiguratorContext =
            updateValidationConfiguratorContext({
              validationConfiguratorContext: this.validationConfiguratorContext,
              showValidationSnackbar: false,
            });
        }}
      ></fg-message-snack-bar>
    `;
  }

  static styles: CSSResultGroup = [
    buttonStyles,
    rootVariablesStyles,
    css`
      .configurator {
        display: flex;
        flex-direction: column;
      }

      .configurator:focus {
        outline: none;
      }

      .wrapper:has([part='retail']) .configurator {
        gap: 16px;
      }

      .wrapper:has([part='wholesale']) .configurator {
        gap: 32px;
      }

      .wrapper:has([part='retail']) .config-step {
        background-color: #fff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        box-sizing: border-box;
        padding: 12px 16px;
      }

      .wrapper:has([part='retail']) .config-step.valid {
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .wrapper:has([part='retail']) .config-step.active {
        border: 2px solid var(--fg-primary, var(--fg-fallback-primary));
        position: relative;
      }

      .wrapper:has([part='retail']) .configurator {
        gap: 16px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .wrapper:has([part='wholesale']):not(.oneColumn) {
          display: grid;
          grid-template-columns: repeat(2, minmax(100px, 50%));
          width: 100%;
          gap: 24px;
        }

        .delimiter {
          display: block;
          width: 100%;
          height: 1px;
          background-color: var(--fg-gray--300, var(--fg-fallback-gray--300));
        }

        .wrapper:has([part='retail']) .config-step.active::after {
          box-shadow: 0 0 60px 0 rgba(0, 0, 0, 0.5);
          content: '';
          display: block;
          height: 100%;
          left: 0;
          position: absolute;
          top: 0;
          width: 100%;
          z-index: -1;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .wrapper {
          display: grid;
          grid-template-rows: max-content auto max-content;
          grid-template-columns: 100%;
          transform: translateY(100%);
          height: 100dvh;
          width: 100vw;
          position: fixed;
          overflow-y: hidden;
          box-sizing: border-box;
          opacity: 0;
          background-color: #fff;
          z-index: 30;
        }

        .wrapper.show {
          opacity: 1;
          transform: translateY(0);
          inset: 0;
        }

        .wrapper.settled {
          transform: unset;
        }

        .wrapper:has([part='retail']) .config-step {
          border: none;
          background-color: transparent;
        }

        .configurator {
          flex-direction: row;
          gap: 0;
          scroll-snap-type: x mandatory;
          position: relative;
          height: auto;
          align-items: flex-start;
          overflow: hidden;
          margin-bottom: 70px;
        }

        .config-step {
          min-width: 100%;
          scroll-snap-align: start;
          padding: 16px 16px 70px;
          box-sizing: border-box;
          height: fit-content;
          max-height: 100%;
          overflow-y: scroll;
        }

        .wrapper:has([part='wholesale']) .config-step {
          padding: 16px 16px 100px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .wrapper:has([part='wholesale']):not(.oneColumn) {
          grid-template-columns: auto minmax(300px, 30%);
        }
      }
    `,
  ];
}
